// src/SmartifyLogin.js
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FetchFromServer, UpdateToServer } from "../api/ServerApi";
import { HOME_ROUTE } from "../appMode";
import { sanitizeMobileInput } from "../utils/mobileNumber";

// --- Helper Functions ---

const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const ensureDeviceId = () => {
  let deviceId = localStorage.getItem("DEVICE_ID");

  // 🔥 force our own format only
  if (!deviceId || !deviceId.startsWith("WEB-")) {
    deviceId = "WEB-" + generateUUID();
    localStorage.setItem("DEVICE_ID", deviceId);
  }

  return deviceId;
};

// --- Main Component ---

const SmartifyLogin = () => {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  /* -------- already signed in? go straight through --------
   * Reaching the login form with a live session means re-typing a password for
   * no reason, so the stored session is honoured instead. This is what makes
   * a returning user look "auto logged in": the session in localStorage is the
   * session, and nothing here has to be re-confirmed to use it.
   *
   * replace:true so Back does not bounce them into the form they just skipped.
   */
  useEffect(() => {
    let signedIn = false;
    try {
      signedIn = !!localStorage.getItem("UserId");
    } catch {
      signedIn = false; // storage unavailable: show the form, it still works
    }
    if (signedIn) navigate(HOME_ROUTE, { replace: true });
  }, [navigate]);

  // --- SECRET REDIRECT LOGIC ---
  const handleSecretClick = () => {
    const code = window.prompt("Enter:");
    if (code === "yfitrams") {
      // Assuming you have set up this route in your App.js
      navigate("/WordManager");
    } else if (code) {
      alert("Invalid code");
    }
  };
  // -----------------------------

  const loginUser = async () => {
    // 1. Basic validation
    if (!mobile || !password) {
      alert("Please fill both fields.");
      return;
    }

    setLoading(true);
    try {
      // 2. Fetch User
      const data = await FetchFromServer(
        "GetUserDetailsForCorporationbyMobileNumberAndPassword",
        {
          MobileNumber: Number(mobile),
          Password: password,
        }
      );


      console.log(data);

      if (!Array.isArray(data) || data.length === 0) {
        alert("Invalid mobile or password.");
        setLoading(false); // Stop loading if invalid
        return;
      }
      
      const user = data[0];

      // 3. DEVICE COMPARISON LOGIC
      const serverDeviceId = user?.ReactDeviceId ?? null;
      const browserDeviceId = ensureDeviceId();

      // 🔴 STRICT BLOCKING LOGIC 🔴
      if (serverDeviceId && serverDeviceId !== browserDeviceId && false) {
        alert("Another session is currently active. Please logout from the other device or contact your admin.");
        setLoading(false);
        return; 
      }

      // 🟢 BINDING LOGIC
      if (!serverDeviceId) {
        await UpdateToServer("UpdateUsers", {
          data: { ReactDeviceId: browserDeviceId },
          where: `UserId = ${user.UserId}`,
        });
      }

      // 4. Login Success - Save Local Data
      localStorage.setItem("UserId", user.UserId);
      localStorage.setItem("MobileNumber", user.MobileNumber);
      // NOTE: the password used to be stored here in plaintext. Nothing reads
      // it, so it was pure risk on a shared field device.
      localStorage.setItem("AllowTesting", user.AllowTesting);
      localStorage.setItem("Name", user.Name);
      localStorage.setItem("Surname", user.Surname);
      localStorage.setItem("SuperAdmin", user.SuperAdmin);
      localStorage.setItem("OtpVerified", "true");

      localStorage.setItem(
        "ThemeColor",
        JSON.stringify({
          back: { r: user.Back_Red, g: user.Back_Green, b: user.Back_Blue },
          button: {
            r: user.Button_Red,
            g: user.Button_Green,
            b: user.Button_Blue,
          },
        })
      );

      // Navigate to whichever search engine is currently active
      navigate(HOME_ROUTE);

    } catch (err) {
      // Full detail stays in the console; the alert gets something an operator
      // in the field can actually act on. Without this, a server 404 dumps a
      // whole Express HTML error page into the alert box.
      console.error("Login error (detailed):", err);

      const raw = String(err?.message || "");
      let message;

      if (/NetworkError|Failed to fetch|AbortError/i.test(raw)) {
        message = "Cannot reach the server. Check your internet connection.";
      } else if (/HTTP 404/.test(raw) || /Cannot POST/i.test(raw)) {
        message =
          "The server is not responding to login right now (it may be restarting). Please wait a minute and try again.";
      } else if (/HTTP 40[13]/.test(raw)) {
        message = "Login was rejected by the server. Please contact support.";
      } else if (/HTTP 5\d\d/.test(raw)) {
        message = "The server hit an error. Please try again in a moment.";
      } else {
        // Strip any HTML the server may have returned so the alert stays readable.
        message = raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 200)
          || "Login failed. Please try again.";
      }

      alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.body}>
      
      {/* --- SECRET BUTTON --- */}
      <button onClick={handleSecretClick} style={styles.secretBtn}>
        ?
      </button>
      {/* --------------------- */}

      <div style={styles.container}>
        <div style={styles.title}>SmartSearch</div>

        <div style={styles.loginBox}>
          <input
            type="tel"
            name="MobileNumber"
            autoComplete="tel"
            inputMode="numeric"
            placeholder="Mobile Number"
            value={mobile}
            onChange={(e) => setMobile(sanitizeMobileInput(e.target.value))}
            maxLength={10}
            style={styles.input}
            disabled={loading}
          />

          <input
            type="password"
            name="Password"
            autoComplete="current-password"
            inputMode="numeric"
            placeholder="4-digit Password"
            value={password}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d{0,4}$/.test(value)) {
                setPassword(value);
              }
            }}
            maxLength={4}
            style={styles.input}
            disabled={loading}
          />

          <button
            onClick={loginUser}
            style={{ ...styles.button, ...styles.login }}
            disabled={loading}
          >
            {loading ? "Please wait..." : "Login"}
          </button>

          <Link to="/ForgotPassword" style={styles.link}>
            Forgotten password?
          </Link>

          <hr />

          <button
            onClick={() => navigate("/signup")}
            style={{ ...styles.button, ...styles.create }}
            disabled={loading}
          >
            Create New Account
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  body: {
    backgroundImage:
      'url("https://wallpapers.com/images/hd/usa-chicago-riverwalk-at-night-1jaq0bwc5dc0g6n0.jpg")',
    backgroundSize: "cover",
    minHeight: "100vh",
    overflowY: "auto",
    position: "relative", // Needed for absolute positioning of secret button
  },
  // New style for the secret button
  secretBtn: {
    position: "absolute",
    top: "20px",
    right: "20px",
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "rgba(255, 255, 255, 0.7)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: "24px 12px",
    boxSizing: "border-box",
  },
  title: {
    fontSize: "48px",
    color: "white",
    fontWeight: "bold",
    marginBottom: "20px",
  },
  loginBox: {
    background: "rgba(255,255,255,0.1)",
    padding: "20px",
    width: "340px",
    borderRadius: "8px",
    textAlign: "center",
  },
  input: {
    width: "100%",
    padding: "14px",
    margin: "8px 0",
    borderRadius: "6px",
    fontSize: "16px",
    boxSizing: "border-box", // Added to prevent padding overflow
  },
  button: {
    width: "100%",
    padding: "14px",
    marginTop: "10px",
    fontWeight: "bold",
    borderRadius: "6px",
    cursor: "pointer",
    border: "none",
  },
  login: { backgroundColor: "#1877f2", color: "white" },
  create: { backgroundColor: "#42b72a", color: "white" },
  link: { color: "white", fontSize: "14px", marginTop: "10px" },
};

export default SmartifyLogin;